// Extracted from original/src/App.tsx:50268-50920 (`{showHistoryModal && (...)}`, the generic "كشف حركات / كشف حساب"
// modal). Only the wallet / vault category is kept (`historyCategory === "wallet"`): the old app forced
// `historyType = "movements"` for vaults and used "account" (with a running balance) for digital boxes; both header
// variants stay behind the `variant` prop because the new statement always carries a ledger running balance
// (D04 §5 "Wallet statement").
// Kept verbatim: backdrop + dialog frame (:50269-50284), header (:50285-50309), filter toolbar (:50310-50545:
// movement-type dropdown with search, from/to EnglishDatePicker, hour dropdown with search, reset button),
// the operator chip (:50548-50561), the "تعديل الفلتر المتطور" button (:50564-50579), the movements counter
// (:50611-50615), loading overlay (:50618-50627), table head/body/empty row (:50628-50894) and footer (:50896-50917).
// [C26] The capital block (:50580-50610, `historyTitle.includes("رأس المال")`) is restored behind `capitalActions`.
// Cut: `movementsWithBalance` /
// `getEffectiveWalletAmounts` arithmetic (:50652-50764 — rows arrive preformatted), the debt-type badge lookup
// (:50671-50679 → `badgeTone` on the row), `formatCustomerStatementItem`, the
// `CustomerReceiptVoucherModal` (:50767-50791 — [C28] the button is restored behind `onVoucher`), the product receipt print button (:50792-50834), `MovementDetailsModal`
// (→ `onRowDetails`), `hasPermission` + `setEditingMovement` / `handleDeleteMovement` (→ optional `onEdit` / `onDelete`;
// the buttons render only when the callback is given), `handlePrintHistory` (→ `onPrint` / `onExportPdf`).
// No money arithmetic: every figure is a string formatted by the container.
import { useState } from 'react';
import { motion } from 'motion/react';
import {
  Check, ChevronDown, Edit2, Eye, FileText, History, Plus, Printer, RotateCcw, SlidersHorizontal, Trash2, TrendingDown, TrendingUp, User, X,
} from 'lucide-react';
import { useI18n } from '../i18n-context.js';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';

/** Badge colours of the old category cell (App.tsx:50671-50679): blue = plain movement, purple = product,
 *  rose = debt ("دين"), emerald = trust ("أمانة"). Defaults to blue. */
export type HistoryBadgeTone = 'blue' | 'purple' | 'rose' | 'emerald';

export interface HistoryRow {
  readonly id: string;
  /** YYYY-MM-DD, formatted by the container. */
  readonly dateLabel: string;
  /** HH:mm, formatted by the container. */
  readonly timeLabel: string;
  /** Opaque type key (`deposit` | `withdraw` | `transfer_in` | `transfer_out` | ...); not rendered, kept for test ids. */
  readonly typeKey: string;
  /** Text of the category badge (old `getMovementCategoryLabel`). */
  readonly typeLabel: string;
  readonly badgeTone?: HistoryBadgeTone;
  readonly description: string;
  readonly note: string;
  /** Not rendered by the old table (no operator column existed); carried for containers / tests. */
  readonly operator?: string;
  /** Preformatted, "-" when zero (old `formatNumber(...)` / `"-"`). */
  readonly inLabel: string;
  readonly outLabel: string;
  readonly commissionLabel: string;
  /** Running balance (IQD line), formatted by the container; shown in the `account` variant only. */
  readonly balanceLabel: string;
  /** Running balance (USD line, without the "$"), formatted by the container. */
  readonly balanceUsdLabel: string;
}

export interface HistoryFilter {
  /** "" = all; otherwise one of HISTORY_TYPE_OPTIONS. */
  readonly type: string;
  /** YYYY-MM-DD or "". */
  readonly from: string;
  readonly to: string;
  /** Operator display name or "". */
  readonly operator: string;
  /** "" = all hours, otherwise "00".."23". */
  readonly hour: string;
}

export interface HistoryModalProps {
  /** `movements` = History icon + "كشف حركات"; `account` = FileText icon + "كشف حساب" and the balance column. */
  readonly variant: 'movements' | 'account';
  readonly title: string;
  readonly rows: readonly HistoryRow[];
  readonly filter: HistoryFilter;
  readonly onFilterChange: (patch: Partial<HistoryFilter>) => void;
  /** When given, the "تعديل الفلتر المتطور" button renders (old: wallet category only). */
  readonly onOpenAdvancedFilter?: () => void;
  readonly onRowDetails: (rowId: string) => void;
  readonly onEdit?: (rowId: string) => void;
  /** [C28] customer statements: the "وصل قيد" button (p02, D-272); absent elsewhere. */
  readonly onVoucher?: (rowId: string) => void;
  readonly onDelete?: (rowId: string) => void;
  readonly onPrint: () => void;
  readonly onExportPdf: () => void;
  readonly onClose: () => void;
  readonly loading?: boolean;
  /** [C26] App.tsx:50580-50610: the capital history's add / withdraw / capitalize buttons; each renders only when given. */
  readonly capitalActions?: { readonly onAdd?: () => void; readonly onWithdraw?: () => void; readonly onCapitalize?: () => void };
  /** Which empty message to show: no movements at all vs. none in the filtered range. Default `no_movements`. */
  readonly emptyState?: 'no_movements' | 'no_match';
}

/** Old option values (App.tsx:50325-50336); labels come from `history.type_<value>` / `history.type_all`. */
export const HISTORY_TYPE_OPTIONS: readonly string[] = [
  '', 'deposit', 'withdraw', 'incoming', 'outgoing', 'transfer', 'manual_work', 'product', 'trade', 'commission',
];

const BADGE_CLASS: Record<HistoryBadgeTone, string> = {
  purple: 'bg-purple-50 dark:bg-purple-900/10 text-purple-600 dark:text-purple-400',
  blue: 'bg-blue-50 dark:bg-blue-900/10 text-blue-600 dark:text-blue-400',
  rose: 'bg-rose-50 dark:bg-rose-900/10 text-rose-600 dark:text-rose-400',
  emerald: 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-600 dark:text-emerald-400',
};

export function HistoryModal(props: HistoryModalProps) {
  const { t } = useI18n();
  const isAccount = props.variant === 'account';
  const f = props.filter;

  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [typeSearch, setTypeSearch] = useState('');
  const [showHourDropdown, setShowHourDropdown] = useState(false);
  const [hourSearch, setHourSearch] = useState('');

  const typeLabel = (value: string) => (value === '' ? t('history.type_all') : t(`history.type_${value}`));
  const typeOptions = HISTORY_TYPE_OPTIONS.map((value) => ({ value, label: typeLabel(value) }));

  const hourLabel = (value: string) => {
    if (!value) return t('history.all_hours');
    const i = parseInt(value, 10);
    if (isNaN(i)) return value;
    const period = i < 12 ? 'AM' : 'PM';
    const displayHour = i % 12 || 12;
    const hStr = String(i).padStart(2, '0');
    return t('history.hour_option', { period, display: displayHour, hour: hStr });
  };
  const hourOptions = ['', ...Array.from({ length: 24 }).map((_, i) => String(i).padStart(2, '0'))].map((value) => ({
    value,
    label: hourLabel(value),
  }));

  return (
    <div key="history-modal-overlay" className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="history-modal">
      <motion.div
        key="history-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={props.onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
      />
      <motion.div
        key="history-modal-dialog"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="relative bg-white dark:bg-[#0d1117] w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] border border-gray-100 dark:border-[#30363d] dark:digital-border"
      >
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-gray-50/50 dark:bg-[#05070a]">
          <div>
            <h3 className="text-xl font-black text-gray-900 dark:text-[#f0f6fc] flex items-center gap-2 uppercase tracking-tight dark:digital-glow">
              {isAccount ? (
                <FileText className="text-indigo-600 dark:text-indigo-400" />
              ) : (
                <History className="text-blue-600 dark:text-blue-400" />
              )}
              {isAccount ? t('history.account_title', { title: props.title }) : t('history.movements_title', { title: props.title })}
            </h3>
            <p className="text-sm text-gray-500 dark:text-[#8b949e] mt-1 font-bold">
              {t('history.subtitle')}
            </p>
          </div>
          <button
            onClick={props.onClose}
            data-testid="history-close-x"
            className="p-2 hover:bg-gray-200 dark:hover:bg-[#161b22] rounded-full transition-colors text-gray-400 dark:text-[#8b949e] hover:text-gray-600 dark:hover:text-[#f0f6fc]"
          >
            <X size={24} />
          </button>
        </div>
        <div className="px-6 py-4 bg-gray-50/50 dark:bg-[#05070a]/50 border-b border-gray-100 dark:border-[#30363d] flex flex-wrap items-end gap-3 justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            {/* نوع الحركة */}
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-black text-gray-700 dark:text-gray-300">
                {t('history.movement_type')}
              </label>
              <div className="relative">
                <button
                  type="button"
                  data-testid="history-type-toggle"
                  onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                  className="w-[180px] bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-1.5 text-xs font-bold text-gray-800 dark:text-[#f0f6fc] flex justify-between items-center hover:border-blue-400 transition-colors cursor-pointer shadow-sm h-[34px]"
                >
                  <span className="truncate">
                    {HISTORY_TYPE_OPTIONS.includes(f.type) ? typeLabel(f.type) : t('history.select_type')}
                  </span>
                  <ChevronDown size={14} className="text-gray-400 shrink-0 mr-1" />
                </button>

                {showTypeDropdown && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowTypeDropdown(false)} />
                    <div className="absolute top-full right-0 mt-2 w-[240px] bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden max-h-[300px]">
                      <div className="p-3 border-b border-gray-100 dark:border-[#30363d] bg-gray-50/80 dark:bg-[#0d1117] text-right">
                        <input
                          type="text"
                          placeholder={t('history.type_search_placeholder')}
                          className="w-full bg-transparent text-right font-bold text-gray-800 dark:text-[#f0f6fc] text-xs focus:outline-none placeholder-gray-400"
                          value={typeSearch}
                          onChange={(e) => setTypeSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="overflow-y-auto flex-1 custom-scrollbar">
                        <div className="px-4 py-2 bg-white dark:bg-[#161b22] text-gray-400 dark:text-[#8b949e] font-bold border-b border-slate-100 dark:border-[#30363d] text-[11px] text-right">
                          {t('history.type_list_heading')}
                        </div>
                        <div className="divide-y divide-gray-50 dark:divide-[#21262d]">
                          {typeOptions
                            .filter((opt) => (typeSearch ? opt.label.toLowerCase().includes(typeSearch.toLowerCase()) : true))
                            .map((opt) => (
                              <button
                                key={`mov-type-${opt.value}`}
                                type="button"
                                data-testid={`history-type-${opt.value || 'all'}`}
                                className={`w-full text-right px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#21262d] text-xs font-bold flex justify-between items-center transition-colors cursor-pointer ${
                                  f.type === opt.value
                                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-950/30'
                                    : 'text-gray-700 dark:text-[#f0f6fc]'
                                }`}
                                onClick={() => {
                                  props.onFilterChange({ type: opt.value });
                                  setShowTypeDropdown(false);
                                  setTypeSearch('');
                                }}
                              >
                                <span>{opt.label}</span>
                                {f.type === opt.value && (
                                  <Check size={14} className="text-blue-600 dark:text-blue-400" />
                                )}
                              </button>
                            ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* من تاريخ */}
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-black text-gray-700 dark:text-gray-300">
                {t('history.from_date')}
              </label>
              <EnglishDatePicker
                value={f.from}
                onChange={(val) => props.onFilterChange({ from: val })}
                className="!py-1.5 !text-xs"
              />
            </div>

            {/* إلى تاريخ */}
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-black text-gray-700 dark:text-gray-300">
                {t('history.to_date')}
              </label>
              <EnglishDatePicker
                value={f.to}
                onChange={(val) => props.onFilterChange({ to: val })}
                className="!py-1.5 !text-xs"
              />
            </div>

            {/* الساعة والدقيقة */}
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-black text-gray-700 dark:text-gray-300">
                {t('history.hour_label')}
              </label>
              <div className="relative">
                <button
                  type="button"
                  data-testid="history-hour-toggle"
                  onClick={() => setShowHourDropdown(!showHourDropdown)}
                  className="w-[180px] bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-1.5 text-xs font-bold text-gray-800 dark:text-[#f0f6fc] flex justify-between items-center hover:border-blue-400 transition-colors cursor-pointer shadow-sm h-[34px]"
                >
                  <span className="truncate">
                    {hourLabel(f.hour)}
                  </span>
                  <ChevronDown size={14} className="text-gray-400 shrink-0 mr-1" />
                </button>

                {showHourDropdown && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowHourDropdown(false)} />
                    <div className="absolute top-full right-0 mt-2 w-[240px] bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden max-h-[300px]">
                      <div className="p-3 border-b border-gray-100 dark:border-[#30363d] bg-gray-50/80 dark:bg-[#0d1117] text-right">
                        <input
                          type="text"
                          placeholder={t('history.hour_search_placeholder')}
                          className="w-full bg-transparent text-right font-bold text-gray-800 dark:text-[#f0f6fc] text-xs focus:outline-none placeholder-gray-400"
                          value={hourSearch}
                          onChange={(e) => setHourSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="overflow-y-auto flex-1 custom-scrollbar">
                        <div className="px-4 py-2 bg-white dark:bg-[#161b22] text-gray-400 dark:text-[#8b949e] font-bold border-b border-slate-100 dark:border-[#30363d] text-[11px] text-right">
                          {t('history.hour_list_heading')}
                        </div>
                        <div className="divide-y divide-gray-50 dark:divide-[#21262d]">
                          {hourOptions
                            .filter((opt) =>
                              hourSearch
                                ? opt.label.toLowerCase().includes(hourSearch.toLowerCase()) || opt.value.includes(hourSearch)
                                : true
                            )
                            .map((opt) => (
                              <button
                                key={`hour-opt-${opt.value}`}
                                type="button"
                                className={`w-full text-right px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#21262d] text-xs font-bold flex justify-between items-center transition-colors cursor-pointer ${
                                  f.hour === opt.value
                                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-950/30'
                                    : 'text-gray-700 dark:text-[#f0f6fc]'
                                }`}
                                onClick={() => {
                                  props.onFilterChange({ hour: opt.value });
                                  setShowHourDropdown(false);
                                  setHourSearch('');
                                }}
                              >
                                <span>{opt.label}</span>
                                {f.hour === opt.value && (
                                  <Check size={14} className="text-blue-600 dark:text-blue-400" />
                                )}
                              </button>
                            ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* زر إعادة ضبط الفلاتر */}
            <div className="flex flex-col justify-end">
              <button
                type="button"
                data-testid="history-reset"
                onClick={() => props.onFilterChange({ from: '', to: '', operator: '', type: '', hour: '' })}
                className="px-3 py-1.5 bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/60 border border-rose-200 dark:border-rose-800/60 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-sm h-[34px]"
                title={t('history.reset_title')}
              >
                <RotateCcw size={13} />
                <span>{t('history.reset')}</span>
              </button>
            </div>
          </div>

          {/* Active Operator Filter Badge */}
          {f.operator && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/60 rounded-xl text-blue-700 dark:text-blue-300 text-xs font-bold" data-testid="history-operator-chip">
              <User size={13} className="text-blue-500" />
              <span>{t('history.operator_chip', { operator: f.operator })}</span>
              <button
                type="button"
                onClick={() => props.onFilterChange({ operator: '' })}
                className="p-0.5 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-md transition-colors"
                title={t('history.clear_operator')}
              >
                <X size={12} />
              </button>
            </div>
          )}

          {/* Customize Filter Modal Re-open button for Wallets */}
          {props.onOpenAdvancedFilter && (
            <button
              type="button"
              data-testid="history-adjust-filter"
              onClick={props.onOpenAdvancedFilter}
              className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 border border-indigo-200 dark:border-indigo-800/60 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <SlidersHorizontal size={13} />
              <span>{t('history.adjust_filter')}</span>
            </button>
          )}
          {props.capitalActions && (
            <div className="flex items-center gap-2" data-testid="history-capital-actions">
              {props.capitalActions.onAdd && (
                <button
                  type="button"
                  onClick={props.capitalActions.onAdd}
                  data-testid="history-capital-add"
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1 transition-all shadow-sm cursor-pointer"
                >
                  <Plus size={14} />
                  {t('history.capital_add')}
                </button>
              )}
              {props.capitalActions.onWithdraw && (
                <button
                  type="button"
                  onClick={props.capitalActions.onWithdraw}
                  data-testid="history-capital-withdraw"
                  className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold flex items-center gap-1 transition-all shadow-sm cursor-pointer"
                >
                  <TrendingDown size={14} />
                  {t('history.capital_withdraw')}
                </button>
              )}
              {props.capitalActions.onCapitalize && (
                <button
                  type="button"
                  onClick={props.capitalActions.onCapitalize}
                  data-testid="history-capital-capitalize"
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold flex items-center gap-1 transition-all shadow-sm cursor-pointer"
                >
                  <TrendingUp size={14} />
                  {t('history.capital_capitalize')}
                </button>
              )}
            </div>
          )}
          <div className="flex-1" />
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            {t('history.count')}{" "}
            {props.rows.length}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 relative dark:bg-[#0d1117]">
          {props.loading && (
            <div className="absolute inset-0 bg-white/60 dark:bg-[#0d1117]/60 backdrop-blur-[2px] z-10 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm font-black text-blue-900 dark:text-blue-400 uppercase tracking-widest">
                  {t('history.loading')}
                </p>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-right" data-testid="history-table">
              <thead>
                <tr className="text-gray-400 dark:text-[#8b949e] text-[10px] font-black uppercase tracking-widest border-b border-gray-100 dark:border-[#30363d]">
                  <th className="pb-4 font-black">{t('date')}</th>
                  <th className="pb-4 font-black">
                    {t('history.category')}
                  </th>
                  <th className="pb-4 font-black">{t('description')}</th>
                  <th className="pb-4 font-black">{t('note')}</th>
                  <th className="pb-4 font-black">{t('incoming')}</th>
                  <th className="pb-4 font-black">{t('outgoing')}</th>
                  <th className="pb-4 font-black">{t('commission')}</th>
                  {isAccount && (
                    <th className="pb-4 font-black">
                      {t('history.balance')}
                    </th>
                  )}
                  <th className="pb-4 font-black">
                    {t('history.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-[#30363d]">
                {props.rows.map((m) => (
                  <tr
                    key={m.id}
                    data-testid={`history-row-${m.id}`}
                    data-type={m.typeKey}
                    className="text-xs hover:bg-gray-50/50 dark:hover:bg-[#161b22]/50 transition-colors"
                  >
                    <td className="py-4 text-gray-500 dark:text-[#8b949e] whitespace-nowrap font-mono">
                      {m.dateLabel}{" "}
                      {m.timeLabel}
                    </td>
                    <td className="py-4">
                      <span
                        className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-tighter ${BADGE_CLASS[m.badgeTone ?? 'blue']}`}
                      >
                        {m.typeLabel}
                      </span>
                    </td>
                    <td className="py-4 font-black text-gray-800 dark:text-[#f0f6fc] uppercase tracking-tight">
                      {m.description}
                    </td>
                    <td className="py-4 text-gray-500 dark:text-[#8b949e] text-[10px] italic font-bold">
                      {m.note || "-"}
                    </td>
                    <td className="py-4 font-black text-emerald-600 dark:text-emerald-400 font-mono">
                      {m.inLabel}
                    </td>
                    <td className="py-4 font-black text-rose-600 dark:text-rose-400 font-mono">
                      {m.outLabel}
                    </td>
                    <td className="py-4 text-orange-600 dark:text-orange-400 font-black font-mono">
                      {m.commissionLabel}
                    </td>
                    {isAccount && (
                      <td className="py-4 font-black text-blue-700 dark:text-blue-400 font-mono dark:digital-glow">
                        <div>{m.balanceLabel}</div>
                        {m.balanceUsdLabel !== '' && (
                          <div className="text-[10px] text-blue-500 dark:text-blue-400">
                            {m.balanceUsdLabel} $
                          </div>
                        )}
                      </td>
                    )}
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        {props.onVoucher && (
                          // [C28] restored: the voucher button of :50767-50791 (D-272, p02)
                          <button
                            onClick={() => props.onVoucher?.(m.id)}
                            data-testid={`history-voucher-${m.id}`}
                            className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors cursor-pointer flex items-center gap-1 font-bold text-[11px]"
                            title={t('history.voucher_title')}
                          >
                            <FileText size={16} />
                            <span className="hidden sm:inline">{t('history.voucher')}</span>
                          </button>
                        )}
                        <button
                          onClick={() => props.onRowDetails(m.id)}
                          data-testid={`history-details-${m.id}`}
                          className="p-1.5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors cursor-pointer"
                          title={t('history.details_title')}
                        >
                          <Eye size={16} />
                        </button>
                        {props.onEdit && (
                          <button
                            onClick={() => props.onEdit?.(m.id)}
                            data-testid={`history-edit-${m.id}`}
                            className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          >
                            <Edit2 size={16} />
                          </button>
                        )}
                        {props.onDelete && (
                          <button
                            onClick={() => props.onDelete?.(m.id)}
                            data-testid={`history-delete-${m.id}`}
                            className="p-1.5 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {props.rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="py-12 text-center text-gray-400 dark:text-[#8b949e]"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <History size={48} className="opacity-20" />
                        <p className="font-bold">
                          {props.emptyState === 'no_match' ? t('history.no_movements_in_range') : t('history.no_movements')}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="p-6 bg-gray-50 dark:bg-[#05070a] border-t border-gray-100 dark:border-[#30363d] flex flex-wrap justify-end gap-3">
          <button
            onClick={props.onPrint}
            data-testid="history-print"
            className="px-6 py-2 bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] text-gray-700 dark:text-[#f0f6fc] rounded-xl font-black uppercase tracking-widest hover:bg-gray-100 dark:hover:bg-[#161b22] transition-all flex items-center gap-2"
          >
            <Printer size={18} />
            {t('history.print')}
          </button>
          <button
            onClick={props.onExportPdf}
            data-testid="history-export-pdf"
            className="px-6 py-2 bg-emerald-600 text-white rounded-xl font-black uppercase tracking-widest hover:bg-emerald-700 transition-all flex items-center gap-2 shadow-lg shadow-emerald-600/20"
          >
            <FileText size={18} />
            {t('history.export_pdf')}
          </button>
          <button
            onClick={props.onClose}
            data-testid="history-close"
            className="px-8 py-2 bg-blue-600 text-white rounded-xl font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20"
          >
            {t('close')}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
